import '../../i18n/autoTranslate';
// NotificationStrip.jsx
// Contextual notification strip for AI Coach.
//
// FIX (layout): The previous version wrapped content in shadcn's <Alert>,
// whose internal CSS grid (grid-cols-[auto_1fr]) and absolutely-positioned
// icon slot were collapsing the message into a tiny column on the left
// while the right half stayed empty and the action buttons overlapped the
// text. We now render a plain styled <div> so we fully control the layout:
// icon | message (takes all remaining space) | actions, all on one row from
// sm+ and stacked on mobile. Message wraps naturally and uses the full
// available width.
//
// FIX (state): Dismissal is owned by the parent (index.jsx). The strip
// just calls onDismiss; it doesn't keep duplicate localStorage state.

import React from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';

export default function NotificationStrip({
  title = 'AI Coach',
  message = `Your last session shows a drop in problem-solving. Try the "Stress Interview" mode today.`,
  onAction,
  onDismiss,
  actionLabel = 'Try Stress Interview',
  show = true,
}) {
  if (!show) return null;

  const handleDismiss = () => {
    if (typeof onDismiss === 'function') onDismiss();
  };

  const handleAction = () => {
    if (typeof onAction === 'function') onAction();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.25)]"
    >
      {/* subtle gradient wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-purple-500/10 via-transparent to-blue-500/10" />

      {/*
        Layout:
        - Mobile (<sm): stack vertically. Icon + text on top, buttons below.
        - sm+: single row. Icon (auto) | text (1fr, takes ALL remaining space) | actions (auto).
        The min-w-0 on the text column is what allows the message to wrap
        instead of forcing the row wider.
      */}
      <div className="relative z-10 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        {/* Icon */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-400/20 bg-purple-500/10 text-purple-300">
          <Sparkles className="h-5 w-5" />
        </div>

        {/* Text — flex-1 + min-w-0 = take all remaining width, allow wrap */}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">{title}</div>
          <p className="text-sm text-gray-300 leading-relaxed">{message}</p>
        </div>

        {/* Actions — full width on mobile, auto on desktop */}
        <div className="flex shrink-0 items-center gap-2 sm:ml-2">
          {onAction && (
            <button
              type="button"
              onClick={handleAction}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-purple-500/90 px-3 text-sm font-medium text-white transition hover:bg-purple-500 sm:flex-none"
            >
              {actionLabel}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss notification"
            title="Dismiss"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
